import Vue from 'vue'
import * as types from '../mutations'
import CampaignService from '@/services/campaign'
import ProductService from '@/services/products'
import TranslationService from '@/services/translations'

import Api from '@/api'
import AIService from '@/services/ai'
import TabTemplates from '@/templates/tabs'
import utils from '@/utils/generateRandomKey'
import translations from '@/utils/translations'
import defaultEditorConfig, { DEFAULT_AUTH_MESSAGE_COLORS } from '@/utils/default-stc-config'
import { replaceWrongLanguageCodes, removeHtmlTagsFromDividers, migrateTranslations, convertJourneyLocationsToObjects } from '@/utils/configHelpers'
import deepCompare from '@/utils/deepCompare'
import { extractBlocks, syncBlocks, reconstructHtml, removeBlockFromHtml } from '@/utils/htmlBlockExtractor'
import { DYNAMIC_VARIABLES, PLAN_HIDDEN_VARIABLES } from '@/const/dynamicVariables'

const state = {
  campaignId: null,
  campaignCount: 0,
  campaign: {},
  scmFields: [],
  consumerOptions: {},
  productStcData: {},
  initialized: false,
  isCampaignLoaded: false,
  isLandingPageConfigLoaded: false,
  previewLanguage: 'en',
  saving: false,
  publishing: false,
  editorKey: '',
  editingContent: false,
  remoteConfig: {},
  json: {},
  needToUpdateRedirection: false,
  isRedirectionTabEnabled: false,
  products: [],
  currentProduct: {},
  companyLanguages: [],
  aiGenerating: false,
  activeEditorTabId: null,
  hoveredBlockId: null,
  hoverSource: null,
  previewClickEvent: { blockId: null, seq: 0 }
}

function filterTabs (tabs, id) {
  return tabs.filter((tab) => {
    return !tab.includes(id)
  })
}

function isTabInFlow (tabs, id) {
  let isInFlow = false
  tabs.forEach((tab) => {
    if (tab.includes(id)) {
      isInFlow = true
    }
  })
  return isInFlow
}

function chunkArray (items, chunkSize) {
  const chunks = []

  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize))
  }

  return chunks
}

// Writes a per-language value under parent[field]. Empty input (for rich text,
// also empty markup like <p></p>) deletes the key so cleared fields fall back
// to the landing page defaults.
// clearIdenticalCopies (used for URL/image fields): when the value is cleared, other
// languages still holding that exact same value are cleared too — they were just
// copies of it, not real translations.
function setOrClearLocaleField (parent, field, lang, value, isRichText, clearIdenticalCopies) {
  const renderedText = isRichText ? String(value).replace(/<[^>]*>/g, '') : value
  const isEmpty = !value || !String(renderedText).trim()
  const existing = parent[field]
  const current = existing && typeof existing === 'object' ? { ...existing } : {}

  if (isEmpty) {
    const previousValue = current[lang]
    delete current[lang]
    if (clearIdenticalCopies && previousValue !== undefined) {
      Object.keys(current).forEach((otherLang) => {
        if (current[otherLang] === previousValue) {
          delete current[otherLang]
        }
      })
    }
  } else {
    current[lang] = value
  }

  if (Object.keys(current).length > 0) {
    return { ...parent, [field]: current }
  }
  const { [field]: removed, ...rest } = parent
  return rest
}

const UNSAFE_LINK_SCHEMES = /^(?:javascript|data|vbscript|file|blob):/

function isUnsafeLink (value) {
  const withoutSpacing = Array.from(String(value))
    .filter((char) => char.charCodeAt(0) > 32 && char.charCodeAt(0) !== 127)
    .join('')
    .toLowerCase()
  return UNSAFE_LINK_SCHEMES.test(withoutSpacing)
}

// The fields that hold each CMS tool's URL
const CMS_TOOL_URL_FIELDS = {
  button: 'url',
  social: 'url',
  typeform: 'url',
  iframe: 'url',
  image: 'src',
  gallery: 'src',
  video: 'content'
}

// When a CMS tool's URL is cleared, other languages still holding that same old URL are
// cleared too because they were just copies of it. But changing the URL to something else
// leaves other languages alone.
function clearIdenticalCMSToolUrlCopies (toolType, previousData, incomingData, defaultLanguage) {
  const urlField = CMS_TOOL_URL_FIELDS[toolType]
  if (!urlField) return
  const previousValues = previousData && previousData[urlField]
  const incomingValues = incomingData && incomingData[urlField]
  if (!previousValues || typeof previousValues !== 'object') return
  if (!incomingValues || typeof incomingValues !== 'object') return
  const previousDefaultValue = previousValues[defaultLanguage]
  if (!previousDefaultValue || incomingValues[defaultLanguage]) return
  Object.keys(incomingValues).forEach((lang) => {
    if (lang !== defaultLanguage && incomingValues[lang] === previousDefaultValue) {
      delete incomingValues[lang]
    }
  })
}

const getters = {
  currentProduct: (state) => state.currentProduct,
  previewLanguage: (state) => state.previewLanguage,
  campaignId: (state) => state.campaignId,
  hasManyCampaigns: (state) => state.campaignCount > 1,
  campaignName: (state) => state.campaign.name,
  productCount: (state) => state.campaign.product_count,
  configJson: (state) => state.json,
  configVersion: (state) => state.json.editor && state.json.editor.version ? state.json.editor.version : 2,
  initialized: (state) => state.initialized,
  isCampaignLoaded: (state) => state.isCampaignLoaded,
  isLandingPageConfigLoaded: (state) => state.isLandingPageConfigLoaded,
  saving: (state) => state.saving,
  publishing: (state) => state.publishing,
  consumerOptions: (state) => state.consumerOptions,
  editingContent: (state) => state.editingContent,
  isRedirectionTabEnabled: (state) => state.isRedirectionTabEnabled,
  needToUpdateRedirection: (state) => state.needToUpdateRedirection,
  products: (state) => state.products,

  redirectionMode: (state, getters) => {
    if (getters.currentPlan && getters.currentPlan.interface.mode !== 'epac') {
      return 'stc'
    }
    if (state.consumerOptions && state.consumerOptions.is_enabled && state.consumerOptions.consumer_url) {
      if (state.consumerOptions.consumer_url.indexOf(Api.default_url) > -1) {
        return 'stc'
      } else if (state.consumerOptions.consumer_url === '{product_url}') {
        return 'product'
      } else {
        return 'url'
      }
    } else if (state.consumerOptions && !state.consumerOptions.is_enabled) {
      return 'product'
    } else {
      return 'url'
    }
  },

  redirectUrl: (state) => {
    if (state.consumerOptions && state.consumerOptions.consumer_url) {
      return state.consumerOptions.consumer_url
    }
  },
  previewUrl: (state) => {
    if (state.json && state.json.editor) {
      if (state.json.editor.preview_url) {
        return state.json.editor.preview_url
      }
    }
    return Api.editor_url
  },
  productImageDisplayMode: (state) => state.json.editor['product-infos'].display_mode || 'contain',
  productStcData: (state) => {
    const propArray = []
    for (const key in state.productStcData) {
      propArray.push({ name: key, id: key, value: state.productStcData[key] })
    }
    return propArray
  },
  editor: (state) => {
    if (state.json && state.json.editor) {
      return state.json.editor
    }
    return {}
  },
  published: (state) => {
    if (state.json && state.json.published && state.json.published.theme) {
      return state.json.published
    }
    return []
  },
  hasPublishedVersion: (state) => {
    if (state.consumerOptions && state.json && state.json.published && state.json.published.theme) {
      return true
    }
    return false
  },
  hasUnpublishedChanges: (state) => {
    if (state.json && state.json.editor && state.json.published) {
      const filteredEditor = { ...state.json.editor }
      delete filteredEditor.reviewed_languages

      const filteredPublished = { ...state.json.published }
      delete filteredPublished.reviewed_languages

      if (!deepCompare(filteredEditor, filteredPublished)) {
        return true
      }
    }
    return false
  },
  editorKey: (state) => state.editorKey,
  stcConfig: (state) => state.json,
  dynamicVariablesAndScmFields: (state) => {
    const customFields = state.scmFields.filter((field) => !field.is_archived)
    return DYNAMIC_VARIABLES.concat(customFields)
  },

  availableVariables: (state, getters) => {
    const planId = (getters.currentPlan && getters.currentPlan.id) || ''
    const hiddenKeys = new Set()
    Object.entries(PLAN_HIDDEN_VARIABLES).forEach(([prefix, keys]) => {
      if (planId.startsWith(prefix)) keys.forEach((k) => hiddenKeys.add(k))
    })

    const vars = DYNAMIC_VARIABLES
      .filter(({ key }) => !hiddenKeys.has(key))
      .map(({ key, name }) => ({ path: key, name }))
    state.scmFields
      .filter((field) => !field.is_archived)
      .forEach((field) => {
        if (!vars.find((v) => v.path === field.key)) {
          vars.push({ path: field.key, name: field.name })
        }
      })
    getters.productStcData
      .filter((entry) => !String(entry.id).startsWith('scantrust_'))
      .forEach((entry) => {
        vars.push({ path: '#' + entry.id, name: entry.name })
      })
    return vars
  },

  apiKey: (state) => state.consumerOptions.api_key,

  findDynamicVariable: (state) => (key) => {
    return DYNAMIC_VARIABLES.find((field) => field.key === key) ||
      state.scmFields.find((field) => field.key === key && !field.is_archived)
  },

  getTheme: (state) => state.json.editor.theme,
  getThemeDefaultFont: (state) => (state.json.editor && state.json.editor.theme && state.json.editor.theme.default_font) || null,
  getThemeLanguageFonts: (state) => (state.json.editor && state.json.editor.theme && state.json.editor.theme.language_fonts) || {},
  getFooter: (state) => (state.json.editor && state.json.editor.footer) || {},
  getCookie: (state) => (state.json.editor && state.json.editor.cookie) || {},
  getAuthAppBranding: (state) => state.json.editor['auth-app-branding'],
  getAuthAppDefaultFont: (state) => (state.json.editor['auth-app-branding'] && state.json.editor['auth-app-branding'].default_font) || null,
  getAuthAppLanguageFonts: (state) => (state.json.editor['auth-app-branding'] && state.json.editor['auth-app-branding'].language_fonts) || {},

  pageTitle: (state) => state.json.editor.title,
  logo: (state) => state.json.editor.logo,
  getJourney: (state) => state.json.editor['track-and-trace'].journey,

  stcTabs: (state) => {
    if (state.json) {
      const tabs = []
      state.json.editor.tabs.forEach((tab) => {
        const tabObject = state.json.editor[tab]
        if (tabObject && tabObject.type) {
          tabs.push({ name: tabObject.label, icon: tabObject.icon.indexOf('$') === 0 ? tabObject.icon : '$' + tabObject.icon, type: tabObject.type, id: tab })
        }
      })
      return tabs
    }
  },

  getTabVisibility: (state) => (id) => {
    return {
      genuine: isTabInFlow(state.json.editor.tab_flow.genuine, id),
      active: isTabInFlow(state.json.editor.tab_flow.active, id),
      fake: isTabInFlow(state.json.editor.tab_flow.fake, id)
    }
  },

  getTabData: (state) => (id) => {
    return state.json.editor[id]
  },

  getTabName: (state) => (id) => {
    return state.json.editor[id].label[state.json.editor.default_language]
  },

  getDefaultTabName: (state) => (id) => {
    return state.json.editor[id].label[state.json.editor.default_language]
  },

  showSerialNumber: (state) => !state.json.editor['product-infos'].hide_serial_number,

  authMessage: (state) => {
    return {
      genuine: state.json.editor['product-infos'].auth_message.genuine[state.json.editor.default_language],
      active: state.json.editor['product-infos'].auth_message.active[state.json.editor.default_language],
      fake: state.json.editor['product-infos'].auth_message.fake[state.json.editor.default_language]
    }
  },

  authMessageStyles: (state) => {
    return state.json.editor['product-infos'].auth_message_styles || JSON.parse(JSON.stringify(DEFAULT_AUTH_MESSAGE_COLORS))
  },

  // Legacy; no longer used
  availableLanguages: (state) => state.json.editor.available_languages,

  enabledLanguages: (state) => state.json.editor.enabled_languages,

  defaultLanguage: (state) => state.json.editor.default_language,

  remoteConfig: (state) => state.remoteConfig,

  getLPKey: (state) => state.remoteConfig.key,

  enableBranding: (state) => {
    if (state.json.editor['product-infos'].hide_hint) {
      return false
    }
    return true
  },

  getToolCount: (state) => (tool) => {
    let count = 0
    state.json.editor.tabs.forEach((tab) => {
      if (~tab.indexOf('cms') && state.json.editor[tab]) {
        state.json.editor[tab].body.forEach((tabTool) => {
          if (tabTool.type === tool) {
            count++
          }
        })
      }
    })
    return count
  },

  hasElabelWidget: (state) => {
    if (!state.json || !state.json.editor || !state.json.editor.tabs) return false
    return state.json.editor.tabs.some((tab) => {
      if (!~tab.indexOf('cms') || !state.json.editor[tab]) return false
      return state.json.editor[tab].body.some((tool) => tool.type && tool.type.startsWith('elabel_'))
    })
  },

  companyLanguages: (state) => state.companyLanguages,
  aiGenerating: (state) => state.aiGenerating,
  activeEditorTabId: (state) => state.activeEditorTabId,
  activeEditorTab: (state) => {
    if (state.activeEditorTabId && state.json && state.json.editor && state.json.editor[state.activeEditorTabId]) {
      return state.json.editor[state.activeEditorTabId]
    }
    return null
  },

  getHtmlBlocks: (state) => (tabId) => {
    const tab = state.json.editor[tabId]
    return tab ? tab._htmlBlocks || {} : {}
  },

  getBlocksMeta: (state) => (tabId) => {
    const tab = state.json.editor[tabId]
    return tab ? tab._blocksMeta || {} : {}
  },

  hoveredBlockId: (state) => state.hoveredBlockId,
  hoverSource: (state) => state.hoverSource,
  previewClickEvent: (state) => state.previewClickEvent,

  hasHtmlSnapshot: (state) => (tabId) => {
    const tab = state.json.editor[tabId]
    return !!(tab && tab.html_old !== undefined && tab.html_old !== null)
  }
}

const actions = {

  async loadCurrentProduct ({ commit }, id) {
    console.log('load current P')
    const res = await ProductService.loadProduct(id)
    commit(types.SET_CURRENT_PRODUCT, res)
  },

  // Edit Config Actions
  changeRedirectionMode ({ commit, getters }, { mode, url }) {
    if (getters.redirectionMode === mode) {
      commit(types.SET_CAMPAIGN_REDIRECTION_STATUS, false)
    } else {
      commit(types.SET_CAMPAIGN_REDIRECTION_STATUS, mode)
    }

    if (mode === 'stc') {
      commit(types.UPDATE_REDIRECTION_URL, Api.default_url)
    } else {
      commit(types.UPDATE_REDIRECTION_URL, '')
    }
  },

  updateRedirectionUrl ({ commit, getters }, url) {
    commit(types.UPDATE_REDIRECTION_URL, url)
    if (!getters.needToUpdateRedirection) {
      commit(types.SET_CAMPAIGN_REDIRECTION_STATUS, 'url')
    }
  },

  async saveRedirectionSettings ({ commit, getters }) {
    let res = {}
    if (getters.needToUpdateRedirection === 'url' || getters.needToUpdateRedirection === 'stc') {
      res = await CampaignService.updateRedirectUrl(state.campaignId, getters.redirectUrl, true)
    } else {
      res = await CampaignService.updateRedirectUrl(state.campaignId, getters.redirectUrl, false)
    }
    commit(types.UPDATE_CONSUMER_OPTIONS, res)
    commit(types.SET_CAMPAIGN_REDIRECTION_STATUS, false)
  },

  async loadLandingPageConfig ({ commit, getters, dispatch }, id) {
    const res = await CampaignService.loadConfig(id)

    if (res && res.id) {
      if (!res.config_json.editor) {
        console.log('init')
        commit(types.INITIALIZING)
        dispatch('initializeConfigEditor', id)
      } else {
        let config = res
        const existingConfigVersion = config.config_json.version || config.config_json.editor.version

        if (existingConfigVersion && existingConfigVersion <= 3) {
          config = replaceWrongLanguageCodes(config)
          config = removeHtmlTagsFromDividers(config)
          config = migrateTranslations(config)
          config = convertJourneyLocationsToObjects(config, getters.findDynamicVariable)

          config.config_json.editor.version = defaultEditorConfig.version
          if (config.config_json.published.version) {
            config.config_json.published.version = defaultEditorConfig.version
          }
          if (config.config_json.version) {
            delete config.config_json.version
          }
        }

        commit(types.SET_REMOTE_STC_CONFIG, config)
        commit(types.INITIALIZED)
      }
    }
    commit(types.IS_LANDING_PAGE_CONFIG_LOADED)
  },

  async initializeConfigEditor ({ commit, dispatch }, id) {
    const config = {
      editor: defaultEditorConfig,
      published: []
    }

    await dispatch('updateRemoteConfig', { config, id })
    commit(types.RELOAD_EDITOR, utils.generateRandomKey())
    commit(types.INITIALIZED)
  },

  async updateRemoteConfig ({ commit }, { config, id }) {
    const res = await CampaignService.updateRemoteConfig({ config, id })
    commit(types.SET_REMOTE_STC_CONFIG, res)
  },

  async saveRemoteConfig ({ commit, getters }) {
    commit(types.SAVING)
    const config = getters.remoteConfig
    const res = await CampaignService.updateRemoteConfig({ config: getters.configJson, id: config.id })
    console.log('remote config', res)
    commit(types.RELOAD_EDITOR, utils.generateRandomKey())
    commit(types.SAVED)
  },

  async remotePublish ({ commit, getters }, languages = null) {
    const publishData = JSON.parse(JSON.stringify(getters.configJson.editor))

    if (languages) {
      const existingReviewedLanguages = new Set(getters.published?.reviewed_languages || [])

      if (languages.length) {
        languages.forEach((lang) => {
          existingReviewedLanguages.add(lang)
        })
      }

      publishData.reviewed_languages = [...existingReviewedLanguages]
    }

    const remote = getters.remoteConfig
    remote.config_json.published = publishData

    await CampaignService.updateRemoteConfig({ config: remote.config_json, id: remote.id })
    commit(types.SET_PUBLISHED_DATA, { published: remote.config_json.published })
    // commit(types.SET_REMOTE_STC_CONFIG_JSON, res)
    commit(types.PUBLISHED)
  },

  // **********************

  async updateAdminSettings ({ commit }, { redirectUrl, previewUrl }) {
    const requestJSON = JSON.parse(JSON.stringify(state.json))
    requestJSON.editor.preview_url = previewUrl
    await CampaignService.updateCampaignSettings(state.campaignId, redirectUrl, requestJSON)
    commit(types.SET_STC_REDIRECT_URL, { redirectUrl, previewUrl })
  },

  async initializeEditor ({ commit, dispatch }) {
    const config = {
      editor: defaultEditorConfig,
      published: []
    }

    await dispatch('updateStcConfig', config)
    commit(types.RELOAD_EDITOR, utils.generateRandomKey())
    commit(types.INITIALIZED)
  },

  async restorePublishedVersion ({ commit, getters, dispatch }, { config }) {
    const json = getters.stcConfig
    json.editor = json.published

    await dispatch('updateStcConfig', { config, json })
  },

  async loadCampaignCount ({ commit }) {
    const count = await CampaignService.loadCampaigns()
    commit(types.SET_CAMPAIGN_COUNT, count)
  },

  async loadCampaign ({ commit, dispatch }, { id, mode }) {
    if (mode !== 'remote') {
      commit(types.INITIALIZING)
    }

    const { consumerOptions, scmFields, campaignData } = await CampaignService.loadCampaign(id)

    const products = await ProductService.loadProducts(id)
    let productStcData
    if (products.length) {
      productStcData = await ProductService.loadProductStcData(products[0].id)
    }

    commit(types.SET_PRODUCTS_LIST, products)
    commit(types.CAMPAIGN_LOADED, { consumerOptions, scmFields, id, productStcData, campaignData, mode })

    // Might need to clean this up later and migrate everything to a remote config
    if (mode !== 'remote') {
      commit(types.SET_CONFIG_JSON, { json: consumerOptions.stc_config })
    }

    if (!consumerOptions.stc_config.editor && mode !== 'remote') {
      dispatch('initializeEditor')
    } else if (mode !== 'remote') {
      console.log('initalized')
      commit(types.INITIALIZED)
    }
    commit(types.IS_CAMPAIGN_LOADED)
  },

  async updateStcConfig ({ commit }, { config, json }) {
    console.log('params', config, json)
    // let res = await CampaignService.updateCampaignOptions(state.campaignId, json)
    const res = await CampaignService.updateRemoteConfig({ config: json, id: config })

    console.log('-----------------------')
    console.log(res)
    commit(types.SET_REMOTE_STC_CONFIG, res)
  },

  updateConfigStrings ({ commit, getters }, { languages, strings, hasUsedMachineTranslate = false }) {
    const updatedConfig = translations.updateConfigStringsLogic(getters.editor, languages, strings, getters.defaultLanguage, hasUsedMachineTranslate)

    commit(types.UPDATE_EDITOR_STRINGS, updatedConfig)
  },

  async updateStcEditor ({ commit }) {
    commit(types.SAVING)
    await CampaignService.updateCampaignOptions(state.campaignId, state.json)
    // commit(types.SET_STC_CONFIG, res)
    commit(types.RELOAD_EDITOR, utils.generateRandomKey())
    commit(types.SAVED)
  },

  async machineTranslateLanguages ({ commit }, { destinations, source, strings }) {
    const machineTranslateBatchSize = 30
    const destinationBatches = chunkArray(destinations || [], machineTranslateBatchSize)
    const preservedLinkTags = {}
    const preservedDynamicVariables = {}
    const processedStringsByKey = {}
    const translatedStrings = {}

    if (!destinationBatches.length) {
      return Promise.resolve({})
    }

    for (const [key, value] of Object.entries(strings)) {
      let text = value == null ? '' : String(value)

      // We preserve <a> tags and dynamic variables by replacing them with special brackets and a number, both of which are unlikely to be translated by Google Translate.
      // For example: the dynamic variable {product_name} becomes ⟦#0⟧ (it would be 0 if its the first/only dynamic variable in the string, 1 if it's the second variable, etc.)

      // Replace <a> tags with placeholders before translation, then restore them afterward.
      preservedLinkTags[key] = []
      text = text.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (match, attrs, inner) => {
        const index = preservedLinkTags[key].length
        const openTag = `<a${attrs}>`
        preservedLinkTags[key].push(openTag)
        return `⟦@${index}⟧${inner}⟦/@${index}⟧`
      })

      // Replace dynamic variables with placeholders
      preservedDynamicVariables[key] = []
      const processedText = text.replace(/\{[^}]+\}/g, (match) => {
        const index = preservedDynamicVariables[key].length
        preservedDynamicVariables[key].push(match)
        return `⟦#${index}⟧`
      })

      processedStringsByKey[key] = processedText
      translatedStrings[key] = {}
    }

    const translateBatch = async (batchIndex = 0) => {
      if (batchIndex >= destinationBatches.length) {
        return
      }

      const batchDestinations = destinationBatches[batchIndex]
      const promiseArray = []

      for (const [key, processedText] of Object.entries(processedStringsByKey)) {
        promiseArray.push(TranslationService.translateString({ destinations: batchDestinations, source, text: processedText, key }))
      }

      const res = await Promise.allSettled(promiseArray)
      res.forEach((data) => {
        if (data.status !== 'fulfilled') {
          console.log('request failed', data)
          return
        }

        translatedStrings[data.value.key] = translatedStrings[data.value.key] || {}
        data.value.translations.forEach((translation) => {
          if (translation.lang === source) {
            translatedStrings[data.value.key][source] = strings[data.value.key]
            return
          }

          const rawText = typeof translation.result === 'string' ? translation.result : ''
          let cleanedText = rawText.replace(/&\*/g, '')

          // Restore <a> tags from placeholders
          const openingLinkTags = preservedLinkTags[data.value.key] || []
          openingLinkTags.forEach((openTag, index) => {
            const linkPlaceholderPattern = new RegExp(`⟦\\s*@\\s*${index}\\s*⟧([\\s\\S]*?)⟦\\s*\\/\\s*@\\s*${index}\\s*⟧`, 'g')
            cleanedText = cleanedText.replace(linkPlaceholderPattern, (match, inner) => `${openTag}${inner}</a>`)
          })

          // Restore dynamic variables from placeholders
          const variableMappings = preservedDynamicVariables[data.value.key] || []
          variableMappings.forEach((originalVar, index) => {
            const placeholderPattern = new RegExp(`⟦\\s*#\\s*${index}\\s*⟧`, 'g')
            cleanedText = cleanedText.replace(placeholderPattern, originalVar)
          })

          translatedStrings[data.value.key][translation.lang] = translation.status === 'ok' ? cleanedText : ''
        })
      })

      await translateBatch(batchIndex + 1)
    }

    await translateBatch()

    return Promise.resolve(translatedStrings)
  },

  updateTabName ({ commit }, { tabId, name }) {
    commit(types.UPDATE_TAB_NAME, { tabId, name })
  },

  updateTabIcon ({ commit }, { tabId, icon }) {
    commit(types.UPDATE_TAB_ICON, { tabId, icon })
  },

  updateTabBody ({ commit }, { tabId, body }) {
    commit(types.UPDATE_TAB_BODY, { tabId, body })
  },

  updateTabBodyContent ({ commit }, { tabId, toolKey, data }) {
    commit(types.UPDATE_TAB_BODY_CONTENT, { tabId, toolKey, data })
  },

  removeTabBody ({ commit }, { toolKey, tabId }) {
    commit(types.REMOVE_TAB_BODY_CONTENT, { toolKey, tabId })
  },

  updateTabFlow ({ commit, getters }) {
    // update visibility order
    const newFlow = {
      active: [],
      fake: [],
      genuine: []
    }

    getters.stcTabs.forEach((tab) => {
      const visibility = getters.getTabVisibility(tab.id)
      if (visibility.active) {
        newFlow.active.push([tab.id])
      }

      if (visibility.fake) {
        newFlow.fake.push([tab.id])
      }

      if (visibility.genuine) {
        newFlow.genuine.push([tab.id])
      }
    })

    commit(types.UPDATE_TAB_FLOW, newFlow)
  },

  updateTabOrder ({ commit, dispatch }, tabs) {
    dispatch('updateTabFlow')
    commit(types.UPDATE_TAB_ORDER, tabs)
  },

  updateTabVisibility ({ commit, dispatch }, { tabId, visibility }) {
    commit(types.UPDATE_TAB_VISIBILITY, { tabId, visibility })
    dispatch('updateTabFlow')
    // dispatch('updateTabOrder', getters.stcTabs)
  },

  updateJourneyStepData ({ commit, dispatch }, { data, index }) {
    commit(types.UPDATE_JOURNEY_STEP_DATA, { data, index })
    if (!state.saving) {
      dispatch('updateStcEditor')
    }
  },

  deleteJourneyStep ({ commit }, { index }) {
    commit(types.DELETE_JOURNEY_STEP, index)
  },

  addJourneyStep ({ commit }, { stepData }) {
    commit(types.ADD_JOURNEY_STEP, stepData)
  },

  addNewTab ({ commit }, { type }) {
    if (type === 'cms') {
      const key = utils.generateRandomKey()
      commit(types.ADD_NEW_TAB, { key: `cms_${key}`, template: TabTemplates[type] })
    } else if (type === 'track-and-trace') {
      commit(types.ADD_NEW_TAB, { key: 'track-and-trace', template: TabTemplates[type] })
    } else if (type === 'custom-html') {
      const key = utils.generateRandomKey()
      commit(types.ADD_NEW_TAB, { key: `html_${key}`, template: TabTemplates[type] })
    }
  },

  deleteTab ({ commit }, id) {
    commit(types.DELETE_TAB, id)
  },

  updateTabAuthMessage ({ commit }, messages) {
    commit(types.UPDATE_AUTH_MESSAGES, messages)
  },

  updateAuthMessageStyles ({ commit }, { status, styles }) {
    commit(types.UPDATE_AUTH_MESSAGE_STYLES, { status, styles })
  },

  updateLogo ({ commit }, logo) {
    commit(types.UPDATE_LOGO, logo)
  },

  overrideComponentStyle ({ commit }, { toolKey, tabId, styles }) {
    for (const key in styles) {
      if (key.indexOf('-') > 0) {
        const camelCase = key.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
        styles[camelCase] = styles[key]
        delete styles[key]
      }
    }
    commit(types.OVERRIDE_COMPONENT_STYLES, { toolKey, tabId, styles })
  },

  async publish ({ commit }) {
    console.log('PUBLISH')
    const json = JSON.parse(JSON.stringify(state.json))
    json.published = json.editor
    commit(types.PUBLISHING)
    await CampaignService.updateCampaignOptions(state.campaignId, json)
    commit(types.SET_PUBLISHED_DATA, { published: json.published })
    commit(types.PUBLISHED)
  },

  updateCustomHtmlContent ({ commit, dispatch }, { tabId, html }) {
    commit(types.UPDATE_CUSTOM_HTML_CONTENT, { tabId, html })
    dispatch('extractHtmlBlocks', { tabId })
  },

  extractHtmlBlocks ({ commit, getters }, { tabId }) {
    const tabData = getters.getTabData(tabId)
    const lang = getters.defaultLanguage
    const html = (tabData && tabData.html) || ''
    const existingBlocks = (tabData && tabData._htmlBlocks) || {}
    const existingMeta = (tabData && tabData._blocksMeta) || {}

    const { blocks, meta } = Object.keys(existingBlocks).length
      ? syncBlocks(html, existingBlocks, existingMeta, lang)
      : extractBlocks(html, lang)

    commit(types.SET_HTML_BLOCKS, { tabId, blocks })
    commit(types.SET_BLOCKS_META, { tabId, meta })
  },

  updateHtmlBlock ({ commit, getters }, { tabId, blockId, value }) {
    const lang = getters.defaultLanguage
    commit(types.UPDATE_HTML_BLOCK, { tabId, blockId, lang, value })

    // Reconstruct full HTML from blocks
    const tabData = getters.getTabData(tabId)
    const defaultHtml = tabData.html || ''
    const html = reconstructHtml(defaultHtml, tabData._htmlBlocks, tabData._blocksMeta, lang, lang)
    commit(types.UPDATE_CUSTOM_HTML_CONTENT, { tabId, html })
  },

  deleteHtmlBlock ({ commit, getters }, { tabId, blockId }) {
    const lang = getters.defaultLanguage
    const tabData = getters.getTabData(tabId)
    const meta = (tabData && tabData._blocksMeta) || {}
    const blockMeta = meta[blockId]
    if (!blockMeta) return

    // For grouped blocks (links), collect all IDs in the same group
    const blockIdsToDelete = []
    if (blockMeta.group) {
      for (const [id, m] of Object.entries(meta)) {
        if (m.group === blockMeta.group) blockIdsToDelete.push(id)
      }
    } else {
      blockIdsToDelete.push(blockId)
    }

    // Remove element from HTML
    const currentHtml = tabData.html || ''
    const newHtml = removeBlockFromHtml(currentHtml, meta, blockId)
    commit(types.UPDATE_CUSTOM_HTML_CONTENT, { tabId, html: newHtml })

    // Filter out deleted blocks before re-syncing
    const existingBlocks = { ...(tabData._htmlBlocks || {}) }
    const existingMeta = { ...(tabData._blocksMeta || {}) }
    for (const id of blockIdsToDelete) {
      delete existingBlocks[id]
      delete existingMeta[id]
    }

    // Re-extract to reset indices and preserve translations
    const { blocks, meta: freshMeta } = Object.keys(existingBlocks).length
      ? syncBlocks(newHtml, existingBlocks, existingMeta, lang)
      : extractBlocks(newHtml, lang)
    commit(types.SET_HTML_BLOCKS, { tabId, blocks })
    commit(types.SET_BLOCKS_META, { tabId, meta: freshMeta })
  },

  async generateAIHtml ({ commit, getters, state }, { tabId, lang, prompt, history }) {
    commit(types.SET_AI_GENERATING, true)
    try {
      // Send current HTML so the backend can modify it (stateless)
      const currentHtml = getters.getTabData(tabId).html || ''
      const { html, summary, message } = await AIService.generateHtml({
        prompt,
        html: currentHtml || undefined,
        lang,
        context: {
          brand_name: getters.campaignName,
          external_variables: [
            ...state.scmFields.filter(f => !f.is_archived).map(f => f.key),
            ...Object.keys(state.productStcData).map(k => `#${k}`)
          ]
        },
        history
      })

      if (html) {
        // Save snapshot only now that we're about to overwrite (so undo goes to a meaningful state)
        commit(types.SAVE_HTML_SNAPSHOT, { tabId })
        commit(types.UPDATE_CUSTOM_HTML_CONTENT, { tabId, html })

        const tabData = getters.getTabData(tabId)
        const existingBlocks = (tabData && tabData._htmlBlocks) || {}
        const existingMeta = (tabData && tabData._blocksMeta) || {}
        const { blocks, meta } = Object.keys(existingBlocks).length
          ? syncBlocks(html, existingBlocks, existingMeta, lang)
          : extractBlocks(html, lang)
        commit(types.SET_HTML_BLOCKS, { tabId, blocks })
        commit(types.SET_BLOCKS_META, { tabId, meta })

        return { html, summary: summary || '', message: '' }
      }

      if (message) {
        return { html: '', summary: '', message }
      }

      throw new Error('AI returned an empty response')
    } finally {
      commit(types.SET_AI_GENERATING, false)
    }
  },

  async loadCompanyLanguages ({ commit }) {
    const languages = await TranslationService.loadCompanyLanguages()
    commit(types.SET_COMPANY_LANGUAGES, languages)
  }
}

const mutations = {

  [types.SET_CURRENT_PRODUCT] (state, product) {
    state.currentProduct = product
  },

  [types.SET_PREVIEW_LANGUAGE] (state, lang) {
    state.previewLanguage = lang
  },

  [types.UPDATE_EDITOR_STRINGS] (state, config) {
    state.json.editor = { ...config }
  },

  [types.SET_PRODUCTS_LIST] (state, products) {
    state.products = products
  },

  [types.SET_PRODUCT_STC_DATA] (state, data) {
    state.productStcData = data
  },

  [types.SET_CAMPAIGN_COUNT] (state, count) {
    state.campaignCount = count
  },

  // Redirection
  [types.ENABLE_REDIRECTION_TAB] (state) {
    state.isRedirectionTabEnabled = true
  },

  [types.SET_CAMPAIGN_REDIRECTION_STATUS] (state, status) {
    state.needToUpdateRedirection = status
  },

  [types.UPDATE_REDIRECTION_URL] (state, url) {
    state.consumerOptions.consumer_url = url
  },

  // Remote Config
  [types.SET_REMOTE_STC_CONFIG] (state, config) {
    state.remoteConfig = config
    state.json = config.config_json
  },

  // ************

  [types.SET_STC_REDIRECT_URL] (state, options) {
    state.json.editor.preview_url = options.previewUrl
    state.consumerOptions.consumer_url = options.redirectUrl
  },

  [types.SET_BRANDING_DISPLAY_MODE] (state, mode) {
    // Set to opposite because "enabled" on editor is "hide" on STC
    const hideHint = { hide_hint: !mode }
    state.json.editor['product-infos'] = { ...state.json.editor['product-infos'], ...hideHint }
  },

  [types.SET_PRODUCT_DISPLAY_MODE] (state, mode) {
    state.json.editor['product-infos'].display_mode = mode
  },

  [types.SET_TRACK_AND_TRACE_JOURNEY] (state, journey) {
    state.json.editor['track-and-trace'].journey = journey
  },

  [types.RELOAD_EDITOR] (state, key) {
    state.editorKey = key
  },

  [types.INITIALIZING] () {
    state.initialized = false
  },

  [types.INITIALIZED] () {
    state.initialized = true
  },

  [types.ENTER_EDIT_MODE] () {
    state.editingContent = true
  },

  [types.LEAVE_EDIT_MODE] () {
    state.editingContent = false
  },

  [types.SET_CONFIG_JSON] (state, { json }) {
    if (json && json.editor && !json.editor.version) {
      json.editor.version = 2
    }
    state.json = json
  },

  [types.UPDATE_CONSUMER_OPTIONS] (state, consumerOptions) {
    state.consumerOptions = consumerOptions
  },

  [types.CAMPAIGN_LOADED] (state, data) {
    state.campaignId = data.id
    state.consumerOptions = data.consumerOptions
    state.scmFields = data.scmFields
    state.productStcData = data.productStcData
    console.log('data !+!+!++!!+')
    console.log(data)
    state.campaign = data.campaignData
  },

  [types.IS_CAMPAIGN_LOADED] () {
    state.isCampaignLoaded = true
  },

  [types.IS_LANDING_PAGE_CONFIG_LOADED] () {
    state.isLandingPageConfigLoaded = true
  },

  [types.SET_PUBLISHED_DATA] (state, published) {
    state.json = { ...state.json, ...published }
  },

  [types.UPDATE_THEME_COLOR] (state, color) {
    const theme = { primary: color, accent: color }
    state.json.editor.theme = { ...state.json.editor.theme, ...theme }
  },

  [types.UPDATE_THEME_TEXT_COLOR] (state, color) {
    const theme = { text: color }
    state.json.editor.theme = { ...state.json.editor.theme, ...theme }
  },

  [types.UPDATE_THEME_BACKGROUND_COLOR] (state, color) {
    const theme = { background: color }
    state.json.editor.theme = { ...state.json.editor.theme, ...theme }
  },

  [types.UPDATE_THEME_DEFAULT_FONT] (state, font) {
    if (font) {
      state.json.editor.theme = { ...state.json.editor.theme, default_font: font }
    } else {
      // eslint-disable-next-line camelcase
      const { default_font, ...rest } = state.json.editor.theme
      state.json.editor.theme = rest
    }
  },

  [types.UPDATE_THEME_LANGUAGE_FONT] (state, { lang, font }) {
    const languageFonts = (state.json.editor.theme && state.json.editor.theme.language_fonts)
      ? { ...state.json.editor.theme.language_fonts }
      : {}

    if (font) {
      languageFonts[lang] = font
    } else {
      delete languageFonts[lang]
    }

    if (Object.keys(languageFonts).length > 0) {
      state.json.editor.theme = { ...state.json.editor.theme, language_fonts: languageFonts }
    } else {
      // eslint-disable-next-line camelcase
      const { language_fonts, ...rest } = state.json.editor.theme
      state.json.editor.theme = rest
    }
  },

  [types.UPDATE_FOOTER_LOCALE_FIELD] (state, { field, lang, value, richText }) {
    if (!state.json.editor.footer) {
      Vue.set(state.json.editor, 'footer', {})
    }
    // only for the URL/image fields: clearing them also clears copies of the same
    // value in other languages (text fields keep their translations)
    const clearIdenticalCopies = (field === 'background' || field === 'privacy') && lang === state.json.editor.default_language
    const savedValue = clearIdenticalCopies && isUnsafeLink(value) ? '' : value
    state.json.editor.footer = setOrClearLocaleField(state.json.editor.footer, field, lang, savedValue, richText, clearIdenticalCopies)
  },

  [types.UPDATE_FOOTER_GDPR_FIELD] (state, { field, lang, value }) {
    if (!state.json.editor.footer) {
      Vue.set(state.json.editor, 'footer', {})
    }
    const footer = state.json.editor.footer
    const gdpr = setOrClearLocaleField(footer.gdpr || {}, field, lang, value)
    if (Object.keys(gdpr).length > 0) {
      state.json.editor.footer = { ...footer, gdpr }
    } else {
      const { gdpr: removed, ...rest } = footer
      state.json.editor.footer = rest
    }
  },

  [types.UPDATE_FOOTER_SETTING] (state, { field, value }) {
    if (!state.json.editor.footer) {
      Vue.set(state.json.editor, 'footer', {})
    }
    state.json.editor.footer = { ...state.json.editor.footer, [field]: value }
  },

  [types.UPDATE_COOKIE_FIELD] (state, { field, lang, value }) {
    if (!state.json.editor.cookie) {
      Vue.set(state.json.editor, 'cookie', {})
    }
    // only for the link URL: clearing it also clears copies of the same value
    // in other languages (text fields keep their translations)
    const clearIdenticalCopies = field === 'link' && lang === state.json.editor.default_language
    const savedValue = clearIdenticalCopies && isUnsafeLink(value) ? '' : value
    state.json.editor.cookie = setOrClearLocaleField(state.json.editor.cookie, field, lang, savedValue, false, clearIdenticalCopies)
  },

  // Removes a deleted link from the other languages too, unless that link has been
  // customized for that language.
  [types.CLEAR_URL_LANGUAGE_COPIES] (state, { section, field, deletedUrl }) {
    const parent = state.json.editor[section]
    const values = parent && parent[field]
    if (!values || typeof values !== 'object' || !deletedUrl) return
    const remaining = { ...values }
    Object.keys(remaining).forEach((lang) => {
      if (remaining[lang] === deletedUrl) {
        delete remaining[lang]
      }
    })
    if (Object.keys(remaining).length > 0) {
      state.json.editor[section] = { ...parent, [field]: remaining }
      return
    }
    const { [field]: removed, ...rest } = parent
    state.json.editor[section] = rest
  },

  [types.UPDATE_AUTH_APP_BACKGROUND_COLOR] (state, color) {
    const authAppBranding = { background: color }
    state.json.editor['auth-app-branding'] = { ...state.json.editor['auth-app-branding'], ...authAppBranding }
  },

  [types.UPDATE_AUTH_APP_PRIMARY_COLOR] (state, color) {
    const authAppBranding = { primary: color }
    state.json.editor['auth-app-branding'] = { ...state.json.editor['auth-app-branding'], ...authAppBranding }
  },

  [types.UPDATE_AUTH_APP_SECONDARY_COLOR] (state, color) {
    const authAppBranding = { secondary: color }
    state.json.editor['auth-app-branding'] = { ...state.json.editor['auth-app-branding'], ...authAppBranding }
  },

  [types.UPDATE_AUTH_APP_BRANDING_LOGO] (state, logoSrc) {
    const authAppBranding = { logo: logoSrc }
    state.json.editor['auth-app-branding'] = { ...state.json.editor['auth-app-branding'], ...authAppBranding }
  },

  [types.UPDATE_AUTH_APP_DEFAULT_FONT] (state, font) {
    const branding = state.json.editor['auth-app-branding'] || {}
    if (font) {
      state.json.editor['auth-app-branding'] = { ...branding, default_font: font }
    } else {
      // eslint-disable-next-line camelcase
      const { default_font, ...rest } = branding
      state.json.editor['auth-app-branding'] = rest
    }
  },

  [types.UPDATE_AUTH_APP_LANGUAGE_FONT] (state, { lang, font }) {
    const branding = state.json.editor['auth-app-branding'] || {}
    const languageFonts = branding.language_fonts ? { ...branding.language_fonts } : {}

    if (font) {
      languageFonts[lang] = font
    } else {
      delete languageFonts[lang]
    }

    if (Object.keys(languageFonts).length > 0) {
      state.json.editor['auth-app-branding'] = { ...branding, language_fonts: languageFonts }
    } else {
      // eslint-disable-next-line camelcase
      const { language_fonts, ...rest } = branding
      state.json.editor['auth-app-branding'] = rest
    }
  },

  [types.UPDATE_AUTH_APP_ADVANCED_SETTINGS] (state, advancedSettings) {
    Vue.set(state.json.editor, 'auth-app-branding', { ...state.json.editor['auth-app-branding'], ...advancedSettings })
    if (deepCompare(state.json.editor['auth-app-branding'], state.json.published['auth-app-branding'])) {
      Vue.set(state.json.published, 'auth-app-branding', { ...state.json.editor['auth-app-branding'], ...advancedSettings })
    }
  },

  [types.OVERRIDE_COMPONENT_STYLES] (state, { toolKey, tabId, styles }) {
    const widgetIndex = state.json.editor[tabId].body.findIndex((e) => e.key === toolKey)

    if (~widgetIndex) {
      Vue.set(state.json.editor[tabId].body[widgetIndex], 'styles', styles)
    }
  },

  [types.SET_SERIAL_NUMBER_DISPLAY] (state, bool) {
    const serialDisplay = { hide_serial_number: !bool }
    state.json.editor['product-infos'] = { ...state.json.editor['product-infos'], ...serialDisplay }
  },

  [types.SET_PAGE_TITLE] (state, title) {
    state.json.editor.title = title
  },

  [types.SET_DEFAULT_LANGUAGE] (state, lang) {
    state.json.editor.default_language = lang

    if (!state.json.editor.enabled_languages.includes(lang)) {
      state.json.editor.enabled_languages.push(lang)
    }

    if (!state.json.editor.reviewed_languages.includes(lang)) {
      state.json.editor.reviewed_languages.push(lang)
    }

    if (state.json.published.enabled_languages && !state.json.published.enabled_languages.includes(lang)) {
      state.json.published.enabled_languages.push(lang)
    }

    if (state.json.published.reviewed_languages && !state.json.published.reviewed_languages.includes(lang)) {
      state.json.published.reviewed_languages.push(lang)
    }
  },

  [types.SET_ENABLED_LANGUAGES] (state, languages) {
    state.json.editor.enabled_languages = [...new Set(languages)]
    if (state.json.published.enabled_languages) {
      state.json.published.enabled_languages = [...new Set(languages)]
    }
  },

  [types.UPDATE_JOURNEY_STEP_DATA] (state, { data, index }) {
    state.json.editor['track-and-trace'].journey[index] = data
  },

  [types.ADD_JOURNEY_STEP] (state, data) {
    state.json.editor['track-and-trace'].journey.push(data)
  },

  [types.DELETE_JOURNEY_STEP] (state, index) {
    state.json.editor['track-and-trace'].journey.splice(index, 1)
  },

  [types.SAVING] (state) {
    state.saving = true
  },

  [types.SAVED] (state) {
    state.saving = false
  },

  [types.PUBLISHING] (state) {
    state.publishing = true
  },

  [types.PUBLISHED] (state) {
    state.publishing = false
  },

  [types.UPDATE_LOGO] (state, logo) {
    state.json.editor = { ...state.json.editor, logo: logo }
  },

  [types.UPDATE_AUTH_MESSAGES] (state, messages) {
    const tmp = {}

    tmp[state.json.editor.default_language] = messages.genuine
    state.json.editor['product-infos'].auth_message.genuine = { ...state.json.editor['product-infos'].auth_message.genuine, ...tmp }

    tmp[state.json.editor.default_language] = messages.active
    state.json.editor['product-infos'].auth_message.active = { ...state.json.editor['product-infos'].auth_message.active, ...tmp }

    tmp[state.json.editor.default_language] = messages.fake
    state.json.editor['product-infos'].auth_message.fake = { ...state.json.editor['product-infos'].auth_message.fake, ...tmp }
  },

  [types.UPDATE_AUTH_MESSAGE_STYLES] (state, { status, styles }) {
    if (!state.json.editor['product-infos'].auth_message_styles) {
      Vue.set(state.json.editor['product-infos'], 'auth_message_styles', JSON.parse(JSON.stringify(DEFAULT_AUTH_MESSAGE_COLORS)))
    }
    Vue.set(state.json.editor['product-infos'].auth_message_styles, status, { ...styles })
  },

  [types.SET_STC_CONFIG] (state, res) {
    state.json = res.stc_config
  },

  [types.UPDATE_TAB_BODY] (state, { tabId, body }) {
    state.json.editor[tabId].body = body
  },

  [types.UPDATE_TAB_BODY_CONTENT] (state, { tabId, toolKey, data }) {
    const index = state.json.editor[tabId].body.findIndex((e) => e.key === toolKey)
    if (~index) {
      const bodyElement = state.json.editor[tabId].body[index]
      const incomingData = JSON.parse(JSON.stringify(data))
      clearIdenticalCMSToolUrlCopies(bodyElement.type, bodyElement.data, incomingData, state.json.editor.default_language)
      bodyElement.data = incomingData
    }
  },

  [types.REMOVE_TAB_BODY_CONTENT] (state, { toolKey, tabId }) {
    state.json.editor[tabId].body = state.json.editor[tabId].body.filter((e) => e.key !== toolKey)
  },

  [types.UPDATE_TAB_NAME] (state, { tabId, name }) {
    const obj = { ...state.json.editor[tabId].label }
    obj[state.json.editor.default_language] = name
    state.json.editor[tabId].label = { ...obj }
  },

  [types.UPDATE_TAB_ICON] (state, { tabId, icon }) {
    const obj = { icon: icon }
    state.json.editor[tabId] = { ...state.json.editor[tabId], ...obj }
  },

  [types.UPDATE_TAB_ORDER] (state, tabs) {
    const tabIds = []
    tabs.forEach((tab) => {
      tabIds.push(tab.id)
    })
    state.json.editor.tabs = tabIds
  },

  [types.ADD_NEW_TAB] (state, { key, template }) {
    const newTab = {}
    newTab[key] = JSON.parse(JSON.stringify(template))
    state.json.editor = { ...state.json.editor, ...newTab }
    state.json.editor.tabs.push(key)
    state.json.editor.tab_flow.genuine.push([key])
    state.json.editor.tab_flow.active.push([key])
    state.json.editor.tab_flow.fake.push([key])
  },

  [types.DELETE_TAB] (state, tabId) {
    state.json.editor.tabs = state.json.editor.tabs.filter(e => e !== tabId)
    state.json.editor.tab_flow.genuine = filterTabs(state.json.editor.tab_flow.genuine, tabId)
    state.json.editor.tab_flow.active = filterTabs(state.json.editor.tab_flow.active, tabId)
    state.json.editor.tab_flow.fake = filterTabs(state.json.editor.tab_flow.fake, tabId)
    delete state.json.editor[tabId]
  },

  [types.UPDATE_TAB_FLOW] (state, flow) {
    state.json.editor.tab_flow = flow
  },

  [types.UPDATE_CUSTOM_HTML_CONTENT] (state, { tabId, html }) {
    Vue.set(state.json.editor[tabId], 'html', html)
  },

  [types.SET_AI_GENERATING] (state, val) {
    state.aiGenerating = val
  },

  [types.SET_ACTIVE_EDITOR_TAB] (state, tabId) {
    state.activeEditorTabId = tabId
  },

  [types.SET_HTML_BLOCKS] (state, { tabId, blocks }) {
    Vue.set(state.json.editor[tabId], '_htmlBlocks', blocks)
  },

  [types.UPDATE_HTML_BLOCK] (state, { tabId, blockId, lang, value }) {
    if (!state.json.editor[tabId]._htmlBlocks) {
      Vue.set(state.json.editor[tabId], '_htmlBlocks', {})
    }
    if (!state.json.editor[tabId]._htmlBlocks[blockId]) {
      Vue.set(state.json.editor[tabId]._htmlBlocks, blockId, {})
    }
    Vue.set(state.json.editor[tabId]._htmlBlocks[blockId], lang, value)
  },

  [types.SET_BLOCKS_META] (state, { tabId, meta }) {
    Vue.set(state.json.editor[tabId], '_blocksMeta', meta)
  },

  [types.DELETE_HTML_BLOCK] (state, { tabId, blockIds }) {
    const tab = state.json.editor[tabId]
    if (!tab) return
    for (const id of blockIds) {
      if (tab._htmlBlocks && tab._htmlBlocks[id]) {
        Vue.delete(tab._htmlBlocks, id)
      }
      if (tab._blocksMeta && tab._blocksMeta[id]) {
        Vue.delete(tab._blocksMeta, id)
      }
    }
  },

  [types.SAVE_HTML_SNAPSHOT] (state, { tabId }) {
    const tab = state.json.editor[tabId]
    if (!tab) return
    Vue.set(tab, 'html_old', tab.html || '')
    Vue.set(tab, '_htmlBlocks_old', JSON.parse(JSON.stringify(tab._htmlBlocks || {})))
    Vue.set(tab, '_blocksMeta_old', JSON.parse(JSON.stringify(tab._blocksMeta || {})))
  },

  [types.SWAP_AI_HTML] (state, { tabId }) {
    const tab = state.json.editor[tabId]
    if (!tab || tab.html_old === undefined) return
    const tmpHtml = tab.html
    const tmpBlocks = JSON.parse(JSON.stringify(tab._htmlBlocks || {}))
    const tmpMeta = JSON.parse(JSON.stringify(tab._blocksMeta || {}))
    Vue.set(tab, 'html', tab.html_old)
    Vue.set(tab, '_htmlBlocks', JSON.parse(JSON.stringify(tab._htmlBlocks_old || {})))
    Vue.set(tab, '_blocksMeta', JSON.parse(JSON.stringify(tab._blocksMeta_old || {})))
    Vue.set(tab, 'html_old', tmpHtml)
    Vue.set(tab, '_htmlBlocks_old', tmpBlocks)
    Vue.set(tab, '_blocksMeta_old', tmpMeta)
  },

  [types.SET_HOVERED_BLOCK_ID] (state, payload) {
    if (payload && typeof payload === 'object') {
      state.hoveredBlockId = payload.blockId
      state.hoverSource = payload.source || null
    } else {
      state.hoveredBlockId = payload
      state.hoverSource = null
    }
  },

  [types.SET_PREVIEW_CLICK_EVENT] (state, blockId) {
    state.previewClickEvent = { blockId, seq: state.previewClickEvent.seq + 1 }
  },

  [types.UPDATE_TAB_VISIBILITY] (state, { tabId, visibility }) {
    if (visibility.genuine) {
      if (!isTabInFlow(state.json.editor.tab_flow.genuine, tabId)) {
        state.json.editor.tab_flow.genuine.push([tabId])
      }
    } else {
      if (isTabInFlow(state.json.editor.tab_flow.genuine, tabId)) {
        state.json.editor.tab_flow.genuine = filterTabs(state.json.editor.tab_flow.genuine, tabId)
      }
    }

    if (visibility.active) {
      if (!isTabInFlow(state.json.editor.tab_flow.active, tabId)) {
        state.json.editor.tab_flow.active.push([tabId])
      }
    } else {
      if (isTabInFlow(state.json.editor.tab_flow.active, tabId)) {
        state.json.editor.tab_flow.active = filterTabs(state.json.editor.tab_flow.active, tabId)
      }
    }

    if (visibility.fake) {
      if (!isTabInFlow(state.json.editor.tab_flow.fake, tabId)) {
        state.json.editor.tab_flow.fake.push([tabId])
      }
    } else {
      if (isTabInFlow(state.json.editor.tab_flow.fake, tabId)) {
        state.json.editor.tab_flow.fake = filterTabs(state.json.editor.tab_flow.fake, tabId)
      }
    }
  },

  [types.SET_CONFIG_VERSION] (state, version) {
    state.json.editor.version = version
  },

  [types.UNPUBLISH_LANGUAGE] (state, lang) {
    const editorIndex = state.json.editor.reviewed_languages.indexOf(lang)
    const publishedIndex = state.json.published.reviewed_languages.indexOf(lang)

    if (~editorIndex) {
      state.json.editor.reviewed_languages.splice(editorIndex, 1)
    }

    if (~publishedIndex) {
      state.json.published.reviewed_languages.splice(publishedIndex, 1)
    }
  },

  [types.APPROVE_LANGUAGE] (state, lang) {
    if (state.json.editor.reviewed_languages.indexOf(lang) === -1) {
      state.json.editor.reviewed_languages.push(lang)
    }
  },

  [types.UNAPPROVE_LANGUAGE] (state, lang) {
    const index = state.json.editor.reviewed_languages.indexOf(lang)

    if (~index) {
      state.json.editor.reviewed_languages.splice(index, 1)
    }
  },

  [types.SET_COMPANY_LANGUAGES] (state, languages) {
    state.companyLanguages = languages
  }

}

export default {
  state,
  getters,
  actions,
  mutations
}
